import React, { useState } from "react";
import { Row, Col } from "antd";

import {
	AddQuoteProductCatalogDrawerFormValues,
	QuoteProduct
} from "@/Contracts/ProposalBuilder/Block";
import {
	Product,
	ProductBillingType,
	ProductMiscEnums,
	distributors,
	ProductType,
	Distributor,
	ProductSource
} from "@/Contracts/Products";

import "../ConfigureDocumentProductDrawer.less";

import SearchDocumentProductCatalog from "../SearchDocumentProductCatalogDrawer/SearchDocumentProductCatalog";
import DocumentProductConfigureDrawerForm from "../DocumentProductConfigureDrawerForm/DocumentProductConfigureDrawerForm";
import { useSelector } from "react-redux";
import { GlobalState } from "@/Reducers/interface";
import { areRequiredFieldsFilledForAddProductToQuoteBlockForm } from "@/V2Utils";
import { getInitialTaxCategoryId } from "@/Components/Utils";
import { AddProductToQuoteBlockDrawerProps } from "./interface";
import ZDrawer from "@molecules/ZDrawer";
import { useFeatureFlag } from "@/Hooks/useFeature";
import {
	getItemViewForProduct,
	resolveCustomFieldsForSelectedPricing
} from "@/Contracts/Products/UnifiedSearchUtils";
import ZImportProductsToQuoteDrawer from "@/ProposalBuilder/components/ZImportProductsToQuoteDrawer";
import { importProductsToQuote } from "@/ProposalBuilder/services/ImportProductsToQuote";
import {
	useProposalBuilderSelector,
	useProposalBuilderDispatch
} from "@/ProposalBuilder/store";
import {
	QuoteBlockData,
	BlockResponse
} from "@/Contracts/ProposalBuilder/Block";
import BlocksActionCreators from "@/ProposalBuilder/store/actions/Blocks";
import ZMessage from "@molecules/ZMessage";
import { useDocumentDisplayCurrency } from "@/ProposalBuilder/hooks/useDocumentDisplayCurrency";
import { convertQuoteProductPricesToQuoteCurrency } from "./convertQuoteProductCurrency";
import { useAutoSubmitQuoteProducts } from "./useAutoSubmitQuoteProducts";

export const AddProductToQuoteBlockDrawer: React.FC<
	AddProductToQuoteBlockDrawerProps
> = props => {
	const {
		taxCategoriesList,
		currentActiveDocument,
		defaultPreference: oldPreferences,
		companyPreferences
	} = useSelector((state: GlobalState) => ({
		taxCategoriesList: state.user.taxCategoriesList,
		currentActiveDocument: state.documents.currentActiveDocument,
		companyPreferences:
			state.user.userCompany.settings.preference_settings
				?.product_preference_settings,
		defaultPreference:
			state.user.userData?.user_settings?.preference_settings
				?.product_preference_settings
	}));

	const [selectedQuoteProduct, setSelectedQuoteProduct] =
		useState<AddQuoteProductCatalogDrawerFormValues | null>(null);
	const [selectedQuoteProducts, setSelectedQuoteProducts] = useState<
		AddQuoteProductCatalogDrawerFormValues[]
	>([]);
	const [
		unfilledSelectedQuoteProductIds,
		setUnfilledSelectedQuoteProductIds
	] = useState<string[]>([]);
	const [isProductAddingToBundle, setIsProductAddingToBundle] =
		useState(false);
	const [isImportDrawerVisible, setIsImportDrawerVisible] = useState(false);

	const reduxBlockId = useProposalBuilderSelector(
		state => state.blocks.currentActiveBlock[0]
	);

	const blockId = props.blockId || reduxBlockId;

	const proposalBuilderDispatch = useProposalBuilderDispatch();

	const movePreferencesToGlobal = useFeatureFlag("move_preferences_global");
	const unifiedProductSearchFlag = useFeatureFlag("unified_product_search");

	const defaultPreference = movePreferencesToGlobal
		? companyPreferences
		: oldPreferences;

	const { isLegacy, effectiveRate } = useDocumentDisplayCurrency();
	const companyToQuoteRate =
		!isLegacy && effectiveRate > 0 ? effectiveRate : 1;

	const handleSubmit = (
		selectedProducts: Product[],
		productVsPricing: Record<string, string | null>,
		addingToBundle = false
	) => {
		setIsProductAddingToBundle(addingToBundle);
		const newSelectedQuoteProducts: AddQuoteProductCatalogDrawerFormValues[] =
			[];
		const newSetUnfilledSelectedQuoteProductIds: string[] = [];
		for (const selectedProduct of selectedProducts) {
			const selectedPricingId = productVsPricing[selectedProduct.id];
			const selectedPricing = selectedProduct.pricing?.find(
				p => p.id === selectedPricingId
			);
			const quoteProductFormValues =
				AddQuoteProductCatalogDrawerFormValues.fromProduct(
					selectedProduct
				);

			quoteProductFormValues.manufacturer =
				selectedProduct.manufacturer ||
				selectedPricing?.manufacturer ||
				"";
			quoteProductFormValues.tax_category_id =
				selectedProduct.tax_category_id ??
				getInitialTaxCategoryId(
					quoteProductFormValues,
					taxCategoriesList,
					currentActiveDocument
				);
			quoteProductFormValues.selected_pricing_id =
				selectedPricing?.id ?? null;
			quoteProductFormValues.selected_supplier_id =
				selectedPricing?.supplier_id;
			quoteProductFormValues.selected_billing_period =
				selectedPricing?.billing_period ?? ProductBillingType.OneTime;
			const costPrice = selectedPricing?.cost_price ?? 0;
			quoteProductFormValues.selected_cost_price = costPrice;
			quoteProductFormValues.selected_setup_price =
				selectedPricing?.setup_price ?? 0;
			quoteProductFormValues.hide_additional_price =
				!quoteProductFormValues?.selected_setup_price;
			quoteProductFormValues.show_individual_product_pricing =
				selectedProduct.show_individual_product_pricing ?? false;
			const markup = defaultPreference?.markup ?? 0;

			const isPricingFromDistributor =
				!!selectedPricing?.distributor_pricing_details;

			// if pricing is coming from Distributor, apply markup to sp else use the custom pricing
			const sellPrice = isPricingFromDistributor
				? costPrice + costPrice * markup * 0.01
				: selectedPricing?.sell_price ?? 0;
			quoteProductFormValues.sell_price = sellPrice;
			quoteProductFormValues.sub_total =
				quoteProductFormValues?.sell_price +
				quoteProductFormValues?.selected_setup_price;
			quoteProductFormValues.selected_sell_price =
				selectedPricing?.sell_price ?? 0;

			if (
				isPricingFromDistributor &&
				selectedPricing &&
				selectedPricing?.name !== ProductMiscEnums.DefaultPricing
			) {
				if (
					selectedPricing.distributor_pricing_details?.distributor ===
					Distributor.CustomDistributor
				) {
					quoteProductFormValues.selected_distributor =
						selectedPricing.distributor_pricing_details?.distributor;

					quoteProductFormValues.selected_custom_distributor_details =
						{
							custom_distributor_id:
								selectedPricing.distributor_pricing_details
									.custom_distributor_pricing_details
									?.custom_distributor.id || "",
							selected_custom_distributor_pricing_id:
								selectedPricing.distributor_pricing_details
									.custom_distributor_pricing_details
									?.custom_distributor_pricing_id || "",
							custom_fields:
								selectedPricing.distributor_pricing_details
									?.custom_distributor_pricing_details
									?.custom_fields ?? []
						};
				} else if (
					selectedPricing.vendor_name &&
					distributors.includes(selectedPricing.vendor_name)
				) {
					quoteProductFormValues.selected_distributor =
						selectedPricing.vendor_name;
				}
			}

			// in the case of self product with mfg number, the product may have vendor name and sku no without the distributor_pricing_details
			quoteProductFormValues.vendor_name =
				selectedPricing?.vendor_name || undefined;
			quoteProductFormValues.vendor_sku_number =
				selectedPricing?.vendor_sku_no || undefined;

			if (unifiedProductSearchFlag) {
				const resolvedItemView = getItemViewForProduct(
					selectedProduct,
					selectedPricingId
				);
				if (resolvedItemView) {
					quoteProductFormValues.name =
						resolvedItemView.name || quoteProductFormValues.name;
					quoteProductFormValues.description =
						resolvedItemView.description ||
						quoteProductFormValues.description;
					if (resolvedItemView.item_images?.length) {
						quoteProductFormValues.item_images =
							resolvedItemView.item_images;
						quoteProductFormValues.selected_images =
							resolvedItemView.item_images;
					}
					quoteProductFormValues.manufacturer_part_no =
						resolvedItemView.manufacturer_part_no ??
						quoteProductFormValues.manufacturer_part_no;
					quoteProductFormValues.manufacturer =
						resolvedItemView.manufacturer ||
						quoteProductFormValues.manufacturer;
					quoteProductFormValues.custom_fields =
						resolveCustomFieldsForSelectedPricing(
							quoteProductFormValues.custom_fields,
							resolvedItemView.custom_fields
						);
				}
			}

			newSelectedQuoteProducts.push({ ...quoteProductFormValues });

			if (
				!areRequiredFieldsFilledForAddProductToQuoteBlockForm(
					quoteProductFormValues,
					taxCategoriesList
				) ||
				quoteProductFormValues.product_source === ProductSource.Amazon
			) {
				newSetUnfilledSelectedQuoteProductIds.push(
					quoteProductFormValues.vendor_product_id
				);
			}
		}
		setUnfilledSelectedQuoteProductIds(
			newSetUnfilledSelectedQuoteProductIds
		);
		if (!addingToBundle) {
			setUnfilledSelectedQuoteProductIds(
				newSetUnfilledSelectedQuoteProductIds
			);
			setSelectedQuoteProducts(newSelectedQuoteProducts);
		} else {
			if (!newSetUnfilledSelectedQuoteProductIds.length)
				props.onSubmit(
					newSelectedQuoteProducts.map(p =>
						convertQuoteProductPricesToQuoteCurrency(
							p,
							companyToQuoteRate
						)
					)
				);
			else setSelectedQuoteProduct(newSelectedQuoteProducts[0]);
		}
	};

	const removeQuoteProducts = (quoteProductIds: string[]) => {
		setSelectedQuoteProducts(
			selectedQuoteProducts.filter(
				quoteProduct =>
					!quoteProductIds.some(
						quoteProductId =>
							quoteProductId === quoteProduct.vendor_product_id
					)
			)
		);
		setUnfilledSelectedQuoteProductIds(
			unfilledSelectedQuoteProductIds.filter(
				id =>
					!quoteProductIds.some(
						quoteProductId => quoteProductId === id
					)
			)
		);
	};

	const resetQuoteProducts = () => {
		setSelectedQuoteProducts([]);
		setUnfilledSelectedQuoteProductIds([]);
	};

	const handleOpenImportDrawer = () => {
		setIsImportDrawerVisible(true);
	};

	const handleImportDrawerClose = () => {
		setIsImportDrawerVisible(false);
	};

	const handleImportConfirmed = async (
		updatedBlock?: BlockResponse,
		sectionName?: string
	) => {
		try {
			if (updatedBlock?.data && "sections" in updatedBlock.data) {
				const quoteBlockData = updatedBlock.data as QuoteBlockData;
				const lastSectionIndex = quoteBlockData.sections.length - 1;

				if (sectionName && lastSectionIndex >= 0) {
					const updatedSections = quoteBlockData.sections.map(
						(section, index) => {
							if (index === lastSectionIndex) {
								return { ...section, name: sectionName };
							}
							return section;
						}
					);

					quoteBlockData.sections = updatedSections;
				}

				if (blockId) {
					await proposalBuilderDispatch(
						BlocksActionCreators.updateBlockData({
							blockData: quoteBlockData,
							blockId: blockId
						})
					);
				}
			}
		} catch (error) {
			console.error("Error importing products:", error);
			ZMessage.error("Failed to import products. Please try again.");
		}
		handleImportDrawerClose();
	};

	const handleConfigureDrawerClose = () => setSelectedQuoteProduct(null);

	const handleConfigureProductSubmit = (quoteProduct: QuoteProduct) => {
		setSelectedQuoteProduct(null);
		const quoteProductIndex = selectedQuoteProducts.findIndex(
			product =>
				quoteProduct.vendor_product_id === product.vendor_product_id
		);
		const newSelectedQuoteProducts = [...selectedQuoteProducts];
		newSelectedQuoteProducts[quoteProductIndex] =
			quoteProduct as AddQuoteProductCatalogDrawerFormValues;
		setSelectedQuoteProducts(newSelectedQuoteProducts);
		setUnfilledSelectedQuoteProductIds(
			unfilledSelectedQuoteProductIds.filter(
				id => id !== quoteProduct.vendor_product_id
			)
		);
	};

	const handleChildProductInBundleSubmit = (quoteProduct: QuoteProduct) => {
		if (selectedQuoteProduct) {
			props.onSubmit([
				convertQuoteProductPricesToQuoteCurrency(
					{
						...selectedQuoteProduct,
						selected_billing_period:
							quoteProduct.selected_billing_period
					},
					companyToQuoteRate
				)
			]);
		}

		handleConfigureDrawerClose();
	};

	useAutoSubmitQuoteProducts(
		selectedQuoteProducts,
		unfilledSelectedQuoteProductIds,
		companyToQuoteRate,
		props.onSubmit
	);

	const isConfigureDrawerVisible = !!selectedQuoteProduct;

	return (
		<>
			<ZDrawer
				visible={props.visible}
				closable={true}
				destroyOnClose={true}
				onClose={props.onClose}
				className="document-product-drawer search-document-product-drawer zomentum-drawer"
				title={
					<Row>
						<Col span={24}>
							<div className="drawer-title-container">
								<div className="drawer-title">
									Search Product
								</div>
							</div>
						</Col>
					</Row>
				}
			>
				<SearchDocumentProductCatalog
					isLoading={false}
					onClose={props.onClose}
					onSubmit={handleSubmit}
					itemType={props.itemType}
					source={props.source}
					isFromProductsPage={false}
					unfilledSelectedQuoteProductIds={
						unfilledSelectedQuoteProductIds
					}
					removeQuoteProducts={removeQuoteProducts}
					resetQuoteProducts={resetQuoteProducts}
					openImportDrawer={handleOpenImportDrawer}
					configureQuoteProduct={(productId: string) =>
						setSelectedQuoteProduct(
							selectedQuoteProducts.find(
								quoteProduct =>
									productId === quoteProduct.vendor_product_id
							) ?? null
						)
					}
					configureDrawer={
						<ZDrawer
							visible={isConfigureDrawerVisible}
							closable={true}
							destroyOnClose={true}
							onClose={handleConfigureDrawerClose}
							className="document-product-drawer add-document-product-drawer zomentum-drawer"
							title={
								<Row>
									<Col span={24}>
										<div className="drawer-title-container">
											<div className="drawer-title">
												Configure Product
											</div>
										</div>
									</Col>
								</Row>
							}
						>
							<DocumentProductConfigureDrawerForm
								okText={
									isProductAddingToBundle
										? "Add Product"
										: "Update Product"
								}
								defaultData={selectedQuoteProduct}
								onClose={handleConfigureDrawerClose}
								onSubmit={
									isProductAddingToBundle
										? handleChildProductInBundleSubmit
										: handleConfigureProductSubmit
								}
								currentActiveDocument={props.document}
								isAddingProduct={true}
								isBundleProduct={
									!!selectedQuoteProduct?.bundled_products
										?.length ||
									selectedQuoteProduct?.product_type ===
										(ProductType as any).Bundle ||
									isProductAddingToBundle
								}
							/>
						</ZDrawer>
					}
				/>
			</ZDrawer>
			{blockId && (
				<ZImportProductsToQuoteDrawer
					visible={isImportDrawerVisible}
					onClose={handleImportDrawerClose}
					blockId={blockId}
					onImportConfirmed={handleImportConfirmed}
					importProductsToQuote={importProductsToQuote}
				/>
			)}
		</>
	);
};
